import { getArrayItemPath } from "./forms/arrayFieldHelpers.js";
import { getStateValue } from "./forms/helpers.js";
import { splitPath } from "./objectPaths.js";
import { parseFields } from "./schemaParser.js";
import { getOptionLabel } from "./selectHelpers.js";
import { isParentField } from "./typeGuards.js";
/**
 * Returns the parent field label for a field.
 *
 * @param parsedField The field to get the label for.
 * @returns The field label for a field.
 */
export function getFieldLabel(parsedField) {
    const fieldSettings = parsedField.settings;
    if (!fieldSettings) {
        return parsedField.description || parsedField.field;
    }
    return fieldSettings.label ? fieldSettings.label : parsedField.field;
}
/**
 * Gets the parent field name from the dependency settings of a dependent field.
 *
 * @param dependencySettings The custom settings for the dependent field.
 * @returns The parent field name from the dependency settings of a dependent
 *   field.
 * @throws Error An error if no dependency settings are found.
 */
export function getParentFieldName(dependencySettings) {
    if (!dependencySettings) {
        throw new Error(`No dependency settings found.`);
    }
    return typeof dependencySettings === "string"
        ? dependencySettings
        : dependencySettings.field;
}
/**
 * Gets the parent field of a dependent field from the list of fields.
 *
 * @param fields All of the fields in the form.
 * @param dependencySettings The custom settings for the dependent field.
 * @param arrayIndex The path of the field to get the parent of. Used for array
 *   items to adjust the parent path appropriately.
 * @returns The parent field of a dependent field from the list of fields.
 */
export function getParentField(fields, dependencySettings, arrayIndex) {
    const fieldName = getParentFieldName(dependencySettings);
    const field = getFields(fields, fieldName);
    return { ...field, path: getArrayItemPath(field, arrayIndex) };
}
/**
 * Gets the field's selected value's pretty label.
 *
 * @param parsedField The field to get the value label for.
 * @param data The current state of the form. Or the field's value.
 * @returns The selected value of the field's pretty label.
 */
export function getValueLabel(parsedField, data) {
    const currentValue = typeof data === "object" ? getStateValue(parsedField.path, data) : data;
    if (currentValue === true || currentValue === false) {
        return getFieldLabel(parsedField);
    }
    if (!("options" in parsedField)) {
        return String(currentValue);
    }
    const chosen = parsedField.options.find((o) => o.value === currentValue);
    if (!chosen) {
        return String(currentValue);
    }
    return getOptionLabel(chosen.description, chosen.value);
}
/**
 * Gets the requested field(s) from the list of fields.
 *
 * @template T Either a single path or an array of paths.
 * @template S The return type of either a ParsedField or ParsedFields depending
 *   on T.
 * @param fields All of the fields in the form.
 * @param fullFieldName The field(s) to get.
 * @returns The requested field(s) from the list of fields.
 * @throws Error If the field is not found in the schema.
 */
export function getFields(fields, fullFieldName) {
    if (typeof fullFieldName !== "string") {
        const output = [];
        for (const field of fullFieldName) {
            const { targetObject, fieldName } = getTargetObject(field, fields);
            const foundField = targetObject.find((f) => f.field === fieldName);
            if (!foundField) {
                throw new Error(`Field ${field} not found in schema.`);
            }
            output.push(foundField);
        }
        return output;
    }
    else {
        const { targetObject, fieldName } = getTargetObject(fullFieldName, fields);
        const field = targetObject.find((f) => f.field === fieldName);
        if (!field) {
            throw new Error(`Field ${fieldName} not found in schema.`);
        }
        return field;
    }
}
/**
 * Gets the nested object indicated by the requested field name.
 *
 * @param fieldName The field name in dot notation.
 * @param fields All of the fields in the form.
 * @returns The nested object that contains the requested field.
 * @throws Error If the field part is not found in the schema.
 * @throws Error If an intermediary field has no children.
 */
export function getTargetObject(fieldName, fields) {
    let targetObject = fields;
    const fieldParts = splitPath(fieldName);
    for (let i = 0; i < fieldParts.length - 1; i++) {
        const field = targetObject.find((f) => f.field === fieldParts[i]);
        if (!field) {
            throw new Error(`Field part ${fieldParts[i]} not found in schema.`);
        }
        if (!isParentField(field)) {
            throw new Error(`Field ${fieldName} has no children.`);
        }
        const { children } = field;
        targetObject = children;
    }
    return { targetObject, fieldName: fieldParts[fieldParts.length - 1] };
}
/**
 * Create a schema and get the fields from it.
 *
 * @template T Either a single path or an array of paths.
 * @template S The return type of either a ParsedField or ParsedFields depending
 *   on T.
 * @param schema The schema to create.
 * @param fieldName The field or field to get from the schema.
 * @returns The field or fields from the schema.
 * @throws Error If the field is not found in the schema.
 */
export function createSchemaAndGetFields(schema, fieldName) {
    if (!("shape" in schema))
        throw new Error("Schema has no shape");
    const fields = parseFields(schema.shape, null);
    return getFields(fields, fieldName);
}
//# sourceMappingURL=accessHelpers.js.map